<template>
  <b-modal
    id="modal_product_price_option"
    hide-header
    hide-header-close
    scrollable
    centered
    :modal-class="{
      'atm-modal-width': isAtm,
    }"
    size="lg"
    :loading="loading"
    @hidden="onHidden"
  >
    <div class="mt-1" />
    <div
      class="position-fixed"
      style="margin-top: -24px; z-index: 10"
    >
      <b-img
        :src="tieredPriceImg"
        width="150"
        height="150"
        class="position-absolute"
        style="top: -15px; left: -20px; object-fit: contain"
      />
    </div>
    <template v-if="loading">
      <div
        class="d-flex justify-content-center align-items-center"
        style="height: 200px"
      >
        <e-spinner />
      </div>
    </template>
    <FormulateForm
      v-else
      ref="formTieredPriceOption"
      name="formTieredPriceOption"
      @submit="onConfirm"
    >
      <b-container class="d-flex flex-column gap-2">
        <b-row>
          <b-col
            md="12"
            class="d-flex gap-2 justify-content-center"
          >
            <div class="product-img">
              <b-img
                thumbnail
                :src="getSaleItemInfo.imageSquare || noImage"
                :alt="`Imagem ${getSaleItemInfo.name || 'sem imagem'}`"
                :style="{ height: '150px', width: '150px', objectFit: 'contain' }"
              />
            </div>

            <div
              class="h-100 d-flex flex-column"
              style="width: 400px"
            >
              <div>
                <p class="h3">
                  {{ getSaleItemInfo.name }}
                </p>
              </div>

              <div class="d-flex gap-2">
                <p class="h4">
                  {{ getSaleItemInfo.price | currency }}
                </p>
                <p
                  v-if="getSaleItemInfo.hasOldPrice"
                  class="h6 product-price-canceled"
                >
                  {{ getSaleItemInfo.oldPrice | currency }}
                </p>
              </div>

              <div
                class="mt-auto justify-content-end"
                style="max-width: 200px"
              >
                <FormulateInput
                  id="modalProductPriceOption_Quantity"
                  v-model="localForm.quantity"
                  type="osk-number-with-buttons"
                  name="modalProductPriceOption_Quantity"
                  :validation="'min:1'"
                  :validation-messages="{
                    min: $t('Quantidade deve ser maior ou igual a 1.'),
                    required: $t('Por favor preencha a quantidade.'),
                  }"
                  :is-osk-enabled="isAtm"
                  :right-disabled="addButtonDisabled"
                  @onInput="onInput"
                  @leftClick="onMinusQuantity"
                  @rightClick="onPlusQuantity"
                />
              </div>
            </div>
          </b-col>
        </b-row>

        <hr class="dropdown-divider">

        <b-row>
          <b-col
            md="12"
            style="overflow-x: auto"
          >
            <FormulateInput
              v-model="localForm.quantity"
              type="number-stepper"
              :options="getTieredPrices"
              :max-value="getSaleItemInfo.stock"
              invalid-option-message="Estoque insuficiente para quantidade selecionada."
              @input="onOptionSelect"
            />
          </b-col>

          <b-col
            md="12"
            class="d-flex flex-column align-items-center"
          >
            <p class="h3">
              {{ getSaleItemInfo.tieredPrice.price | currency('-', 2) }}
            </p>
            <p class="h5">
              A partir de {{ getSaleItemInfo.tieredPrice.quantityFrom }} unidade(s)
            </p>
          </b-col>
        </b-row>
      </b-container>
    </FormulateForm>

    <template #modal-footer>
      <b-row class="w-100 d-flex justify-content-between align-items-center">
        <b-col class="d-flex justify-content-end">
          <e-button
            id="modal_product_price_option-btn_cancel"
            class="mr-1"
            :class="{
              'atm-btn': isAtm,
            }"
            variant="outline-primary"
            :text="$t('Voltar')"
            :text-shortcuts="isAtm ? [] : ['ESC']"
            :busy="loading"
            @click="onCancel"
          />

          <e-button
            id="modal_product_price_option-btn_confirm"
            variant="primary"
            :class="{
              'atm-btn': isAtm,
            }"
            :text="getConfirmButtonText"
            :text-shortcuts="isAtm ? [] : ['CTRL', 'ENTER']"
            :busy="loading"
            @click="onConfirm"
          />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import { BModal, BContainer, BRow, BCol, BImg } from 'bootstrap-vue'
import { keyboardCode, formulateHelper, saleItemUtils } from '@/mixins'
import EButton from '@/views/components/EButton.vue'
import { Shortcuts } from 'shortcuts'
import noImage from '@/assets/images/noimage.png'
import { debounce } from 'lodash'
import ESpinner from '@/views/components/ESpinner.vue'
import tieredPriceImg from '@/assets/images/leve-mais-pague-menos.png'

const getInitialLocalForm = () => ({
  quantity: 1,
  isCancelled: false,
})

export default {
  components: { BModal, BContainer, BRow, BCol, BImg, EButton, ESpinner },

  mixins: [keyboardCode, formulateHelper],

  props: {},

  data() {
    return {
      noImage,
      tieredPriceImg,
      loading: false,
      localSaleItem: null,
      localForm: getInitialLocalForm(),
      isAdd: false,
      shortcuts: new Shortcuts(),
    }
  },
  computed: {
    ...mapGetters('pages/pdv/payBoxConfiguration', ['currentPayboxConfiguration']),
    isAtm() {
      return this.currentPayboxConfiguration?.isAtm
    },

    getConfirmButtonText() {
      const text = this.isAdd ? this.$t('Adicionar') : this.$t('Editar')

      const { priceRuleSelected } = this.localSaleItem || {}
      if (!priceRuleSelected?.priceInfo) return text

      return `${text} (${this.localForm.quantity || 0}) ${this.$options.filters.currency(
        priceRuleSelected.priceInfo.localNetValue,
        '',
        2
      )}`
    },

    getSaleItemInfo() {
      const { skuProduct, priceRuleSelected } = this.localSaleItem || {}
      const { priceInfo, tieredPrice } = priceRuleSelected || {}

      return {
        name: skuProduct?.name || '',
        imageSquare: skuProduct?.imageSquare,
        hasOldPrice: priceInfo?.hasOldPrice,
        oldPrice: priceInfo?.oldPrice,
        price: priceInfo?.price,
        stock: Math.floor(skuProduct?.stock),
        productQuantity: this.localForm.quantity,
        subtotal: priceInfo?.localNetValue,
        tieredPrice: tieredPrice || {
          quantityFrom: 1,
          price: priceInfo?.originalPrice,
        },
      }
    },

    getTieredPrices() {
      return (this.localSaleItem?.skuProduct?.tieredPrices || []).map(tiered => ({
        label: this.$options.filters.currency(tiered.price, '-', 2),
        value: tiered.quantityFrom,
        maxValue: tiered.quantityTo,
      }))
    },

    addButtonDisabled() {
      const stock = saleItemUtils.getStockIfExceedQuantity(
        this.localSaleItem?.skuProduct?.stock,
        this.localForm?.quantity
      )
      return stock > 0
    },
  },

  watch: {},

  methods: {
    resetModal() {
      this.localSaleItem = null
      this.localForm = getInitialLocalForm()
      this.isAdd = false
    },

    isToShowModal({ hasTieredPrices, hasPromotion }) {
      if (hasPromotion) return false
      return hasTieredPrices
    },

    async showModalAsync(productOrKit, { quantity } = {}, fetchProductCallback) {
      this.addShortcuts()
      this.isAdd = !quantity
      this.localForm.quantity = Number(quantity) || 1

      this.$bvModal.show('modal_product_price_option')

      let productOrKitResult = productOrKit
      if (fetchProductCallback) {
        try {
          this.loading = true
          productOrKitResult = await fetchProductCallback()
        } finally {
          this.loading = false
        }
      }

      productOrKitResult.promotionId = null // não adicionar regra de preço para promoção
      this.localSaleItem = saleItemUtils.buildProductToSaleItem(productOrKitResult, {
        quantity: this.localForm.quantity,
      })

      this.focusInput('#modalProductPriceOption_Quantity')

      return new Promise(resolve => {
        this.$once('modal-finish', data => {
          resolve(data)
          this.hideModal()
        })
      })
    },

    onHidden() {
      this.removeShortcuts()
      this.$emit('modal-finish') // pra cenário de "atalho esc" ou "click no overlay"
      this.resetModal()
    },
    hideModal() {
      this.$bvModal.hide('modal_product_price_option')
    },

    onCancel() {
      this.$emit('modal-finish')
    },
    async onConfirm() {
      if (await this.$refs.formTieredPriceOption.hasValidationErrors()) {
        return
      }

      this.$emit('modal-finish', {
        ...this.localForm,
        quantity: Number(this.localForm.quantity),
      })
    },

    onMinusQuantity() {
      if (this.localForm.quantity - 1 < 0) {
        return
      }

      this.localForm.quantity -= 1

      this.onUpdateSaleItem()
    },
    onPlusQuantity() {
      this.localForm.quantity += 1

      this.onUpdateSaleItem()
    },
    onInput() {
      this.onUpdateSaleItem()
    },
    onOptionSelect() {
      this.onUpdateSaleItem()
    },

    // eslint-disable-next-line prefer-arrow-callback, func-names
    onUpdateSaleItem: debounce(function () {
      if (!this.localSaleItem) return

      const quantityInInput = this.localForm.quantity
      const stock = saleItemUtils.getStockIfExceedQuantity(
        this.localSaleItem.skuProduct?.stock,
        this.localForm.quantity
      )
      if (stock) {
        this.localForm.quantity = stock
      }

      this.localSaleItem = saleItemUtils.updateSaleItemOrSaleKit(this.localSaleItem, {
        quantity: this.localForm.quantity,
      })

      const result = saleItemUtils.getQuantityChangeMessageFromSaleItem({
        ...this.localSaleItem,
        quantity: quantityInInput,
      })
      if (result?.message) {
        this.showAtmAlert({
          title: result.title,
          text: result.message,
          icon: 'info',
          timer: null,
        })
      }
    }, 500),

    addShortcuts() {
      const { ctrl, enter } = this.keyCode

      this.shortcuts.add([
        {
          shortcut: [ctrl, enter].join('+'),
          handler: () => {
            this.onConfirm()
            return true
          },
        },
      ])
    },

    removeShortcuts() {
      this.shortcuts.reset()
    },
  },
}
</script>

<style lang="scss" scoped>
.product-img {
  width: 150px;
}
</style>

<style lang="scss">
.atm-modal-width {
  // override bootstrap class
  .modal-dialog {
    max-width: 90% !important;
  }
}

#modal_product_price_option {
  div[data-type='number-stepper'] .formulate-input-errors > .formulate-input-error {
    text-align: center;
  }
}
</style>
